"use client";

import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  PHONE_COUNTRY_CODES,
  getPhoneCountryByCode,
  getDefaultPhoneCountry,
  type PhoneCountryCode,
} from "@/lib/constants/phone-codes";
import { ScrollArea } from "@/components/ui/scroll-area";

// Flag image component using flag CDN service
function FlagImage({ code, className }: { code: string; className?: string }) {
  // Using flagcdn.com for flag images - you can also use other services
  // Format: https://flagcdn.com/16x12/{country-code-lowercase}.png
  const flagUrl = `https://flagcdn.com/24x18/${code.toLowerCase()}.png`;

  return (
    <img
      src={flagUrl}
      alt={`${code} flag`}
      className={cn("inline-block", className)}
      width={24}
      height={18}
      onError={(e) => {
        // Fallback to showing code if image fails to load
        const target = e.target as HTMLImageElement;
        target.style.display = 'none';
        if (target.nextSibling && target.nextSibling.textContent !== code) {
          const span = document.createElement('span');
          span.textContent = code;
          span.className = 'font-medium text-xs';
          target.parentNode?.insertBefore(span, target.nextSibling);
        }
      }}
    />
  );
}

export interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onCountryChange?: (countryCode: string) => void;
  defaultCountry?: string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  error?: boolean;
  id?: string;
  name?: string;
  required?: boolean;
  autoFocus?: boolean;
}

export function PhoneInputWithImages({
  value = "",
  onChange,
  onCountryChange,
  defaultCountry,
  placeholder = "(201) 555-0123",
  disabled = false,
  readOnly = false,
  className,
  error = false,
  id,
  name,
  required = false,
  autoFocus = false,
}: PhoneInputProps) {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const [selectedCountry, setSelectedCountry] = React.useState<string>(() => {
    return defaultCountry || getDefaultPhoneCountry() || "US";
  });
  const [phoneNumber, setPhoneNumber] = React.useState<string>(() => {
    // Extract the local number from the value if it includes a country code
    if (value?.startsWith("+")) {
      const country = PHONE_COUNTRY_CODES.find(c =>
        value.startsWith(c.dialCode)
      );
      if (country) {
        setSelectedCountry(country.code);
        return value.slice(country.dialCode.length).trim();
      }
    }
    return value;
  });

  React.useEffect(() => {
    // Update phone number when value prop changes
    if (value?.startsWith("+")) {
      const country = PHONE_COUNTRY_CODES.find(c =>
        value.startsWith(c.dialCode)
      );
      if (country) {
        setSelectedCountry(country.code);
        setPhoneNumber(value.slice(country.dialCode.length).trim());
        return;
      }
    }
    setPhoneNumber(value || "");
  }, [value]);

  const country = getPhoneCountryByCode(selectedCountry);

  const handleCountrySelect = (countryCode: string) => {
    setSelectedCountry(countryCode);
    setOpen(false);
    setSearchValue("");

    const newCountry = getPhoneCountryByCode(countryCode);
    if (newCountry && onChange) {
      // Construct the full phone number with country code
      const fullNumber = phoneNumber ? `${newCountry.dialCode} ${phoneNumber}` : "";
      onChange(fullNumber);
    }

    if (onCountryChange) {
      onCountryChange(countryCode);
    }
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPhoneNumber = e.target.value;
    setPhoneNumber(newPhoneNumber);

    if (onChange && country) {
      const fullNumber = newPhoneNumber ? `${country.dialCode} ${newPhoneNumber}` : "";
      onChange(fullNumber);
    }
  };

  const filteredCountries = React.useMemo(() => {
    if (!searchValue) return PHONE_COUNTRY_CODES;

    const search = searchValue.toLowerCase();
    return PHONE_COUNTRY_CODES.filter(
      country =>
        country.name.toLowerCase().includes(search) ||
        country.dialCode.includes(search) ||
        country.code.toLowerCase().includes(search)
    );
  }, [searchValue]);

  return (
    <div className={cn(
      "relative flex items-stretch w-full rounded-md border bg-background",
      error && "border-red-500",
      disabled && "opacity-50 cursor-not-allowed",
      className
    )}>
      {/* Country selector integrated in the input */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex items-center gap-1 px-3 border-r hover:bg-accent/50 transition-colors",
              "focus:outline-none focus:ring-0",
              disabled && "pointer-events-none"
            )}
            disabled={disabled || readOnly}
          >
            {country && (
              <>
                <FlagImage code={country.code} className="w-5 h-auto" />
                <ChevronDown className="h-3.5 w-3.5 opacity-60 ml-1" />
              </>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[340px] p-0" align="start">
          <Command shouldFilter={false}>
            <div className="border-b px-3 py-2">
              <input
                placeholder="Search country..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="w-full outline-none text-sm placeholder:text-muted-foreground bg-transparent"
              />
            </div>
            <ScrollArea className="h-[280px]">
              <CommandList>
                {filteredCountries.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No country found.
                  </div>
                ) : (
                  <CommandGroup>
                    {filteredCountries.map((countryOption) => (
                      <CommandItem
                        key={countryOption.code}
                        value={countryOption.code}
                        onSelect={() => handleCountrySelect(countryOption.code)}
                        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-accent"
                      >
                        <FlagImage code={countryOption.code} className="w-6 h-auto flex-shrink-0" />
                        <span className="flex-1 text-sm">{countryOption.name}</span>
                        <span className="text-sm text-muted-foreground">
                          {countryOption.dialCode}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </ScrollArea>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Phone number input */}
      <input
        type="tel"
        id={id}
        name={name}
        value={phoneNumber}
        onChange={handlePhoneNumberChange}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        autoFocus={autoFocus}
        className={cn(
          "flex-1 px-3 py-2 bg-transparent text-sm outline-none placeholder:text-muted-foreground",
          "disabled:cursor-not-allowed"
        )}
      />
    </div>
  );
}

// Export with the same name for easy switching
export { PhoneInputWithImages as PhoneInput };